import { useEffect, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useTelegramDisconnect, useTelegramLink, useTelegramStatus } from "@/api/notify";
import { Icon } from "@/components/Icon";
import { useToast } from "@/components/Toast";
import { Button, Chip } from "@/components/ui";
import { relativeTime } from "@/lib/format";
import { usePush } from "@/lib/push";

/** Profile → Notifications: personal Telegram DMs and browser push for this browser. */
export function NotificationChannelsSection() {
  const { t } = useTranslation();
  return (
    <section className="card flex flex-col gap-4 p-5">
      <div className="flex flex-col gap-1">
        <h2 className="m-0 label-caps">{t("notifications.channels.title")}</h2>
        <p className="m-0 text-sm text-muted">{t("notifications.channels.intro")}</p>
      </div>
      <TelegramRow />
      <div className="border-t border-border" />
      <PushRow />
    </section>
  );
}

function Row({ icon, title, hint, children }: { icon: "send" | "bell"; title: string; hint: ReactNode; children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="flex min-w-[220px] flex-1 items-start gap-3">
        <span className="mt-0.5 text-muted">
          <Icon name={icon} />
        </span>
        <div className="flex flex-col gap-1">
          <p className="m-0 font-medium">{title}</p>
          <div className="text-sm text-muted">{hint}</div>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">{children}</div>
    </div>
  );
}

function TelegramRow() {
  const { t } = useTranslation();
  const toast = useToast();
  const [link, setLink] = useState<string | null>(null);
  const status = useTelegramStatus(link !== null);
  const create = useTelegramLink();
  const disconnect = useTelegramDisconnect();
  const s = status.data;

  // Stop waiting once the bot has linked the chat.
  const wasConnected = useRef<boolean | null>(null);
  useEffect(() => {
    if (!s) return;
    if (wasConnected.current === false && s.connected && link) {
      setLink(null);
      toast.success(t("notifications.channels.connectedToast"));
    }
    wasConnected.current = s.connected;
  }, [s, link, toast, t]);

  if (!s) return null;
  if (!s.available) {
    return <Row icon="send" title={t("notifications.channels.telegramTitle")} hint={t("notifications.channels.telegramOff")}>{null}</Row>;
  }

  const connect = () =>
    create.mutate(undefined, {
      onSuccess: (r) => {
        setLink(r.url);
        window.open(r.url, "_blank", "noopener");
      },
      onError: (e) => toast.error(e),
    });

  if (s.connected) {
    const name = s.username ? `@${s.username}` : "Telegram";
    return (
      <Row
        icon="send"
        title={t("notifications.channels.telegramTitle")}
        hint={
          <span className="flex flex-wrap items-center gap-2">
            <Chip tone="pass" icon="check">{t("notifications.channels.connectedAs", { name })}</Chip>
            {s.linked_at && <span>{t("notifications.channels.connectedSince", { time: relativeTime(s.linked_at) })}</span>}
          </span>
        }
      >
        <Button
          variant="ghost"
          loading={disconnect.isPending}
          onClick={() =>
            disconnect.mutate(undefined, {
              onSuccess: () => toast.success(t("notifications.channels.disconnected")),
              onError: (e) => toast.error(e),
            })
          }
        >
          {t("notifications.channels.disconnect")}
        </Button>
      </Row>
    );
  }

  return (
    <Row
      icon="send"
      title={t("notifications.channels.telegramTitle")}
      hint={
        link ? (
          <span role="status">{t("notifications.channels.waiting")}</span>
        ) : (
          t("notifications.channels.telegramHint", { bot: s.bot_username ?? "" })
        )
      }
    >
      {link ? (
        <>
          <a href={link} target="_blank" rel="noopener" className="text-sm font-medium">
            {t("notifications.channels.openAgain")}
          </a>
          <Button variant="secondary" loading={status.isFetching} onClick={() => void status.refetch()}>
            {t("notifications.channels.checkAgain")}
          </Button>
        </>
      ) : (
        <Button variant="primary" icon="send" loading={create.isPending} onClick={connect}>
          {t("notifications.channels.connect")}
        </Button>
      )}
    </Row>
  );
}

function PushRow() {
  const { t } = useTranslation();
  const push = usePush();
  if (push.state === "loading") return null;

  const hint =
    push.state === "unsupported"
      ? t("notifications.channels.pushUnsupported")
      : push.state === "blocked"
        ? t("notifications.channels.pushBlocked")
        : push.state === "on"
          ? t("notifications.channels.pushOn")
          : t("notifications.channels.pushHint");

  return (
    <Row
      icon="bell"
      title={t("notifications.channels.pushTitle")}
      hint={
        <>
          {hint}
          {push.error != null && (
            <p role="alert" className="m-0 mt-1 text-fail-fg">
              {t("notifications.channels.pushFailed")}
            </p>
          )}
        </>
      }
    >
      {(push.state === "on" || push.state === "off") && (
        <label className="inline-flex cursor-pointer items-center gap-2">
          <input
            type="checkbox"
            role="switch"
            className="peer sr-only"
            checked={push.state === "on"}
            disabled={push.busy}
            onChange={(e) => void (e.target.checked ? push.enable() : push.disable())}
            aria-label={t("notifications.channels.pushToggle")}
          />
          <span
            aria-hidden
            className="relative h-6 w-11 rounded-full bg-border-strong transition-colors peer-checked:bg-accent peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-accent peer-disabled:opacity-55 after:absolute after:top-0.5 after:left-0.5 after:size-5 after:rounded-full after:bg-white after:shadow after:transition-transform peer-checked:after:translate-x-5"
          />
        </label>
      )}
    </Row>
  );
}
